import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AddressModel } from '../../../components/partials/address/models/address.model';
import { BaseController } from '../../../core/abstractions/base.controller';
import { ApiResponseModel } from '../../../models/core/api-response.model';
import { EntityTypeEnum } from '../../../models/enums/entity-type.enum';
import { FormattedAddressRequestModel } from '../../../models/formatted-address-request.model';
import { ListShareholderModel } from '../../../models/list-shareholder.model';
import { ShareholderSummaryModel } from '../../../models/shareholder-summary.model';
import { ShareholderModel } from '../../../models/shareholder.model';
import { SimpleItemModel } from '../../../models/simple-item.model';
import { LocationsResource } from '../../../services/resources/locations.resource';
import { MetaResource } from '../../../services/resources/meta.resource';
import { ShareholdersResource } from '../../../services/resources/shareholders.resource';
import { SharedBusinessLogicService } from '../../../services/shared-business-logic.service';

@Component({
    selector: 'sl-shareholders-form',
    templateUrl: './shareholders-form.component.html',
    styleUrls: ['./shareholders-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ShareholdersFormComponent
    extends BaseController<ShareholderModel, ShareholderSummaryModel, ListShareholderModel>
    implements OnInit
{
    EntityTypeEnum = EntityTypeEnum;
    address = new AddressModel();
    isBusiness = true;
    infomaterials: SimpleItemModel[] = [];
    infomaterialControl = new UntypedFormControl();
    formattedAddress: string;
    readonly metaResource = inject(MetaResource);

    protected override readonly _entityResource = inject(ShareholdersResource);

    private shortSalutationText: string;
    private salutationTitleText: string;

    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly locationsResource = inject(LocationsResource);
    private readonly sharedBusinessLogicService = inject(SharedBusinessLogicService);

    constructor() {
        super();
        effect(
            () => {
                if (!this.isLoading() && !!this.record()) {
                    this.isBusiness = this.sharedBusinessLogicService.isBussinesShareholder(this.record()!.shareholderTypeId);
                    this.formGroup.patchValue(this.record()!);
                }
            },
            { allowSignalWrites: true }
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.setupForm();
        this.loadResources();
    }

    override save(): void {
        super.save();
    }

    selectShareholderType(event: MatAutocompleteSelectedEvent): void {
        this.isBusiness = !event.option || this.sharedBusinessLogicService.isBussinesShareholder(event.option.id);
    }

    selectSalutationTitleText(event: MatAutocompleteSelectedEvent): void {
        this.salutationTitleText = event.option.value;
    }

    selectSalutationText(event: MatAutocompleteSelectedEvent): void {
        this.shortSalutationText = event.option.value;
    }

    clearShareholderType(): void {
        this.isBusiness = true;
    }

    infomaterialChange(event: MatSelectChange): void {
        this.formGroup.controls['infomaterials'].setValue(event.value.id);
    }

    getCountries(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.metaResource.getCountries().pipe(
            map(r => {
                const currentResponse = new ApiResponseModel<SimpleItemModel[]>({
                    actions: r.actions,
                    message: r.message,
                    page: r.page,
                    pageSize: r.pageSize,
                    totalPages: r.totalPages,
                    totalRecords: r.totalRecords,
                    type: r.type
                });
                const newValue: SimpleItemModel[] = [];

                r.value.forEach(c => newValue.push(new SimpleItemModel({ id: c.id, label: c.name })));
                currentResponse.value = newValue;

                return currentResponse;
            })
        );
    }

    updateFormattedAddress(): void {
        if (!!this.address.locationId) {
            this.locationsResource
                .getFormattedAddress(+this.address.locationId, this.getFormattedAddressRequest(this.address.locationId))
                .subscribe(r => (this.formattedAddress = r.value));
        }
    }

    updateLastNameSuppl(value: string): void {
        if (!this.formGroup.controls['hasHyphen'].pristine && value.length < 1) {
            this.formGroup.controls['hasHyphen'].setValue(false);
        }
    }

    private loadResources(): void {
        this.metaResource.getInfomaterials().subscribe(r => {
            this.infomaterials = r.value;
        });
    }

    private getFormattedAddressRequest(locationId: string): FormattedAddressRequestModel {
        return new FormattedAddressRequestModel({
            locationId: +locationId,
            companyDepartment: this.formGroup.value.companyDepartment,
            companyName: this.formGroup.value.companyName,
            firstName: this.formGroup.value.firstName,
            hasHyphen: this.formGroup.value.hasHyphen,
            isConfidential: this.formGroup.value.isConfidential,
            isPoBox: this.address.isPoBox,
            jobTitle: this.formGroup.value.jobTitle,
            languageId: this.formGroup.value.languageId,
            lastName: this.formGroup.value.lastName,
            lastNameSupplement: this.formGroup.value.lastNameSupplement,
            number: this.address.streetNumber,
            poBox: this.address.poBox,
            salutationTitle: this.salutationTitleText,
            shortSalutation: this.shortSalutationText,
            street: this.address.street,
            streetAfterSupplement: this.address.streetAfterSupplement,
            streetSupplement: this.address.streetSupplement
        });
    }

    private setupForm(): void {
        const controlValue = undefined;
        this.formGroup = this.formBuilder.group({
            shareholderTypeId: new FormControl(controlValue, Validators.required),
            ownershipTypeId: new FormControl(controlValue, Validators.required),
            displayName: new FormControl(controlValue, Validators.required),
            companyName: new FormControl(controlValue, undefined),
            companyLegalFormId: new FormControl(controlValue, undefined),
            shortSalutationId: new FormControl(controlValue, undefined),
            salutationTitleId: new FormControl(controlValue, undefined),
            firstName: new FormControl(controlValue, undefined),
            lastName: new FormControl(controlValue, undefined),
            lastNameSupplement: new FormControl(controlValue, undefined),
            hasHyphen: new FormControl(controlValue, undefined),
            companyActivityId: new FormControl(controlValue, undefined),
            dateOfBirth: new FormControl(controlValue, undefined),
            companyDepartment: new FormControl(controlValue, undefined),
            nationalityCountryId: new FormControl(controlValue, undefined),
            jobTitle: new FormControl(controlValue, undefined),
            isManagementOrBoardOfDirectors: new FormControl(controlValue, undefined),
            isEmployee: new FormControl(controlValue, undefined),
            languageId: new FormControl(controlValue, Validators.required),
            deliveryMethodId: new FormControl(controlValue, Validators.required),
            letterSalutationId: new FormControl(controlValue, undefined),
            isConfidential: new FormControl(controlValue, undefined),
            email: new FormControl(controlValue, undefined),
            emailConfirmed: new FormControl(controlValue, undefined),
            mobileNumber: new FormControl(controlValue, undefined),
            mobileNumberConfirmed: new FormControl(controlValue, undefined),
            infomaterials: new FormControl(controlValue, undefined),
            locationId: new FormControl(controlValue, Validators.required),
            street: new FormControl(controlValue, undefined),
            streetNumber: new FormControl(controlValue, undefined),
            streetSupplement: new FormControl(controlValue, undefined),
            streetAfterSupplement: new FormControl(controlValue, undefined),
            isPoBox: new FormControl(controlValue, undefined),
            poBox: new FormControl(controlValue, undefined),
            locationZipCode: new FormControl(controlValue, undefined),
            locationCity: new FormControl(controlValue, undefined),
            countryId: new FormControl(controlValue, undefined)
        });
    }
}
